<template>
  <nav class="mobile-navi" :class="{ 'mobile-navi--open': isMenuOpen }">
    <header class="mobile-navi__header">
      <max-width-container>
        <div class="mobile-navi__header-content">
          <Logo class="mobile-navi__logo" :logo-href="logoHref"></Logo>
          <Leads :show-basket="showBasket" />
          <meta-navi
            is-reduced
            :secondary-links="secondaryLinksMarked"
            :login-links="loginLinks"
          />
          <burger-menu :is-open="isMenuOpen" @click="burgerClicked" />
        </div>
      </max-width-container>
    </header>
    <div
      class="mobile-navi__modal"
      :class="{ 'mobile-navi__modal--open': isMenuOpen }"
    >
      <MainNavi
        v-if="level2Links.length"
        class="mobile-navi__secondary mobile-navi__secondary_navigation"
        data-track-as="navi.secondary"
        :links="level2Links"
        :cta="level2CallToAction"
      />
      <MobileFooter
        :level1-links="level1Links"
        :secondary-links="secondaryLinks"
        :languages="languages"
      />
    </div>
  </nav>
</template>

<script>
import { useFixedOverlay } from '../../../composables/useFixedOverlay'
import Leads from '../common/Leads'
import Logo from '../common/Logo'
import MaxWidthContainer from '../common/MaxWidthContainer'
import MetaNavi from '../common/MetaNavi'
import MainNavi from '../mainNavi/MainNavi'
import BurgerMenu from './BurgerMenu'
import MobileFooter from './MobileFooter'

export default {
  name: 'MobileNavi',
  components: {
    MobileFooter,
    Leads,
    MetaNavi,
    MainNavi,
    Logo,
    MaxWidthContainer,
    BurgerMenu,
  },

  props: {
    showBasket: Boolean,

    level1Links: {
      type: Array,
      required: true,
    },

    level2Links: {
      type: Array,
      default: () => [],
    },

    level2CallToAction: {
      type: Object,
      default: () => null,
    },

    logoHref: {
      type: String,
      default: 'https://sanitas.com',
    },

    loginLinks: {
      type: Object,
      default: () => null,
    },

    secondaryLinks: {
      type: Array,
      default: () => [],
    },

    languages: {
      type: Array,
      default: () => [],
    },

    search: {
      type: Object,
      default: () => {
        return {
          label: undefined,
          url: undefined,
        }
      },
    },
  },
  setup() {
    const { toggle: burgerClicked, isOpen: isMenuOpen } = useFixedOverlay()
    return { burgerClicked, isMenuOpen }
  },
  computed: {
    // We need this for the contact wizard to figure out if the page was
    // opened through the secondary link or another link.
    secondaryLinksMarked() {
      return this.secondaryLinks.map(link => ({
        ...link,
        href: link.href + '?link=secondary',
      }))
    },
  },
}
</script>

<style lang="postcss" scoped>
.mobile-navi {
  position: fixed;
  top: 0;
  width: 100%;
  height: var(--navigation-height);

  z-index: calc(var(--z-index-overlay) - 1);
  /**
   * z-index only applies to elements with a position different than static
   * (https://philipwalton.com/articles/what-no-one-told-you-about-z-index/)
   * we had to use a z-index over z-index-10, because this one is already in use by elements
   * like the chat head and scroll to top button
   */
  background-color: var(--c-primary-neutral-3);
  margin: 0 calc(var(--body-offset-horizontal) * -1);
  padding: 0 var(--body-offset-horizontal);
  transition: height 150ms ease-in-out;

  &--open {
    height: 100vh;
    overflow-x: hidden;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    justify-content: stretch;
  }

  &__modal {
    flex: 1 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    overflow: auto;
    height: calc(100% - var(--navigation-height));
    padding: 0 var(--body-offset-horizontal);
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease-in-out;
  }

  &__modal--open {
    opacity: 1;
    pointer-events: initial;
  }

  &__header {
    display: flex;
    position: relative;
    align-items: center;
    height: var(--navigation-height);
    width: 100%;

    &::after {
      content: '';
      height: 0;
      width: 100vw;
      border-bottom: 1px solid var(--c-secondary-neutral-3);
      position: absolute;
      bottom: 0;
      left: calc(-1 * var(--body-offset-horizontal));
    }
  }

  &__header-content {
    display: flex;
    gap: var(--spacing-6);
    align-items: center;
    width: 100%;
  }

  &__logo {
    flex: 1 100%;
    /* each navigation component as a vertically decreasing z-index because of the popovers */
    z-index: var(--z-index-navigation);
  }

  &__secondary {
    z-index: var(--z-index-flyout);
  }
}

@media (--md) {
  .mobile-navi__header-content {
    align-items: flex-end;
  }
}
</style>
